import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';

const FAQ = () => {
 const [searchTerm, setSearchTerm] = useState('');
 const [activeCategory, setActiveCategory] = useState('All');
 const [openQuestions, setOpenQuestions] = useState({});

 const faqs = [
 // About PopCrown
 {
 category: 'About PopCrown',
 question: 'What is PopCrown Social?',
 answer: "PopCrown Social is the ultimate members-only fan community dedicated to celebrating and preserving a legendary musical legacy. It offers interactive features, exclusive content, and a vibrant community of dedicated fans worldwide."
 },
 {
 category: 'About PopCrown',
 question: 'Who is PopCrown for?',
 answer: "PopCrown is for fans who want something better: a dedicated home for the community, designed to protect privacy, support safety, and preserve the legacy with care and credibility. It's a calmer space, with higher standards, where people can actually connect without the negativity found elsewhere."
 },
 {
 category: 'About PopCrown',
 question: 'What makes PopCrown different from free fan groups?',
 answer: "Unlike free platforms funded by ads and data sales, PopCrown is:\n\n• Ad-free\n• No selling member data\n• No tracking across websites\n• Properly moderated\n• Focused on truth and legacy preservation\n• Built by fans, for fans\n\nThe community funds the community, not advertisers."
 },
 // Membership & Pricing
 {
 category: 'Membership & Pricing',
 question: 'What membership plans are available?',
 answer: " The PopCrown app is FREE — install it from your phone's browser today (App Store and Google Play listings coming soon). You get:\n\n• Daily MJ quizzes & games\n• The Legal Vault (documents & timelines)\n• In His Words (Michael's own quotes)\n• Global leaderboard\n• 130+ languages\n\nNo card needed. No ads. No paywall on the good stuff.\n\n PopCrown Community (optional) — £1.99/month:\n• Join the conversation — community feed with fans in 130+ countries\n• Early access to event tickets\n• Real discount codes — merch, tickets, partner perks\n• Fan Points & exclusive members-only badges\n\nYour PopCrown Community fee keeps the community safe, moderated and legally compliant. Cancel anytime."
 },
 {
 category: 'Membership & Pricing',
 question: 'What is the PopCrown Founders Show?',
 answer: "Coming Soon! The PopCrown Founders will be launching a weekly YouTube show and Podcast featuring:\n\n• Interviews with fans from the community\n• Behind the scenes at PopCrown\n• Album, song, and music video reviews\n• Michael Biopic 2026 discussions\n• And much more!\n\nSubscribe to be notified when we launch!"
 },
 {
 category: 'Membership & Pricing',
 question: 'Why does the PopCrown Community have a membership fee?',
 answer: "Great question. The PopCrown app itself is completely FREE — daily quizzes, the Legal Vault, In His Words, leaderboard, all yours forever with no card needed.\n\nThe PopCrown Community is the optional paid layer (£1.99/month) where members chat, get early event tickets and exclusive discounts. Why charge for that?\n\nMost social platforms feel \"free\" — but they're funded by ads, data tracking, and algorithms designed to keep you scrolling. PopCrown is different.\n\n What your PopCrown Community fee supports\n\n• Servers and hosting (24/7 availability)\n• Security and data protection\n• Moderation that keeps the feed safe\n• App development (iOS, Android, web)\n• Payment processing fees\n• Ongoing maintenance and improvements\n• Legal compliance (UK Online Safety Act and beyond)\n\n PopCrown isn't funded by ads or data sales\n\n• No ads\n• No selling member data\n• No tracking members across other websites and apps\n\nThe community funds the community, not advertisers.\n\n A note on affordability\n\nIf joining the PopCrown Community right now isn't possible, you still get the full app for free — and there are more ways to be involved:\n\n• Sign up to the free newsletter\n• Explore the free content on the PopCrown website\n• Follow PopCrown on social media and share posts that matter\n\nEveryone has a part to play.\n\n The bottom line\n\nThe app is free, forever. PopCrown Community is for fans who want more — a place to belong, a place to compete, and a place that celebrates what matters. For under 7p a day, you keep this place ad-free, safe and properly maintained."
 },
 {
 category: 'Membership & Pricing',
 question: 'What do I get with the free app vs PopCrown Community?',
 answer: " The free app (no card needed, forever):\n\n• Daily Quizzes & Games\n• The Legal Vault — Documents & timelines\n• In His Words — Michael's own quotes\n• Global Leaderboard\n• 130+ languages supported\n\n PopCrown Community members (£1.99/month) get everything above PLUS:\n\n• Community Feed — Connect with fans worldwide\n• Early access to event tickets\n• Real discount codes — merch, tickets, partner perks\n• Fan Points & Exclusive Badges"
 },
 {
 category: 'Membership & Pricing',
 question: 'How do I join the PopCrown Community?',
 answer: "Click any 'Join PopCrown Community' button on this website. You'll be taken to our secure Stripe checkout where you can:\n\n1. Pay £1.99 for your first month\n2. Enter your details\n3. Complete payment securely\n4. Create your account\n5. Start joining the conversation!\n\nYou'll have instant access on web and mobile."
 },
 {
 category: 'Membership & Pricing',
 question: 'How do I cancel my PopCrown Community membership?',
 answer: "Cancel anytime through your account settings on the members site. You'll keep PopCrown Community access until the end of your paid period — and the free app stays yours forever, no matter what. You can also manage subscriptions through Stripe's customer portal."
 },
 {
 category: 'Membership & Pricing',
 question: 'What happens if my PopCrown Community membership expires?',
 answer: "The free app stays yours — quizzes, Legal Vault, In His Words and the leaderboard never go away. You'll only lose access to the PopCrown Community feed, early event tickets and discount codes. Your community profile, posts, and history are saved for 3 months after expiry. Renew within this period to pick up where you left off.\n\n️ After 3 months, inactive community accounts and their data may be permanently deleted."
 },
 // Account
 {
 category: 'Account',
 question: 'I forgot my password, what do I do?',
 answer: "On the sign-in page, click 'Forgot Password' and enter your email. You'll receive a password reset link. Check your spam folder if you don't see it within a few minutes."
 },
 {
 category: 'Account',
 question: 'Can I use PopCrown on multiple devices?',
 answer: "Yes! Your membership works on:\n\n• Desktop web browser\n• Tablet web browser\n• Mobile web browser\n\nWe have a PWA (Progressive Web App) now available for direct install on any device - no App Store needed! Simply visit the site and tap 'Add to Home Screen' for an app-like experience.\n\nDesktop, tablet, and mobile are all fully optimised. Simply sign in with your account on any device."
 },
 {
 category: 'Membership & Pricing',
 question: 'Is there a minimum age to use PopCrown?',
 answer: " The free PopCrown app is available to all ages — no minimum age to download or use the free features (daily quizzes, the Legal Vault, In His Words, the global leaderboard).\n\n You must be at least 16 years old to access the community features of this app, including the PopCrown Community feed, posts, comments, direct messages and any social interaction with other members. This applies to all paid PopCrown Community memberships and is in place to comply with the UK Online Safety Act, UK GDPR and our community safety standards.\n\nBy creating a PopCrown Community account you confirm you are 16 or over. See our Terms & Conditions for full details."
 },
 // Support
 {
 category: 'Support',
 question: 'How do I contact support?',
 answer: "Email us at support@popcrownsocial.com with:\n\n• Your issue or question\n• Screenshots if helpful\n• Your account email\n\nWe read every message and aim to respond within 24-48 hours."
 },
 {
 category: 'Support',
 question: 'How do I suggest a feature or report a bug?',
 answer: "We love hearing from our community! Email support@popcrownsocial.com with your idea or bug report. Include as much detail as possible - your feedback helps us improve PopCrown for everyone."
 },
 // Website Content
 {
 category: 'Website',
 question: 'What free content is available on the website?',
 answer: "The PopCrown website offers free content including:\n\n• Explore Section - Browse the Timeline, Music, News, and more\n• Legal Vault - Access documented facts, court documents, and truth about the allegations\n• Blog - Read fan stories and articles from the community\n• About Us - Meet the team behind PopCrown\n\nNo membership required to browse these sections!"
 },
 {
 category: 'Website',
 question: 'What is the Legal Vault?',
 answer: "The Legal Vault is a comprehensive, fact-based resource containing documented evidence about the allegations and legal cases. It includes:\n\n• Court documents and transcripts\n• FBI investigation results\n• Factual timelines\n• Myth-busting with sources\n\nWe believe in honoring the legacy with truth and documented facts, not tabloid narratives."
 },
 {
 category: 'Website',
 question: 'How do I sign up for the newsletter?',
 answer: "Click any 'Join Newsletter' or 'Stay Updated' button on the website. Enter your name and email address. You'll receive:\n\n• Latest news and updates\n• Community highlights\n• Event announcements\n• Exclusive content previews\n\nIt's completely free!"
 },
 {
 category: 'Website',
 question: 'What is in the Explore section?',
 answer: "The Explore section gives you free access to:\n\n• Timeline - Key moments and milestones\n• Music - Albums and music history\n• Videos - Iconic visual masterpieces\n• News - Latest updates and stories\n• Legal Vault - Facts and documented truth\n• Tourism - Visit significant locations\n\nExplore and learn at your own pace."
 },
 {
 category: 'Website',
 question: 'Can I contribute to the blog?',
 answer: "Yes! We welcome fan stories and contributions from the community. If you'd like to share your story or write for the PopCrown blog, email us at support@popcrownsocial.com with your idea. We love hearing personal stories of how the music and legacy has impacted fans' lives."
 }
 ];

 const categories = ['All', ...new Set(faqs.map(faq => faq.category))];

 const toggleQuestion = (index) => {
 setOpenQuestions(prev => ({
 ...prev,
 [index]: !prev[index]
 }));
 };

 const filteredFaqs = faqs.filter(faq => {
 const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
 faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
 const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
 return matchesSearch && matchesCategory;
 });

 // Schema.org JSON-LD for FAQ Page - Maximum SEO with all questions for rich snippets
 const faqSchema = {
 "@context": "https://schema.org",
 "@type": "FAQPage",
 "name": "PopCrown FAQ - Michael Jackson Fan Community Help",
 "description": "Frequently asked questions about PopCrown, the ultimate Michael Jackson fan community. Learn about membership, features, and how to join the King of Pop fan community.",
 "mainEntity": faqs.slice(0, 10).map(faq => ({
 "@type": "Question",
 "name": faq.question,
 "acceptedAnswer": {
 "@type": "Answer",
 "text": faq.answer.replace(/\n/g, ' ').substring(0, 500)
 }
 }))
 };

 const breadcrumbSchema = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 "itemListElement": [
 {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com"},
 {"@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://popcrownofficial.com/faq"}
 ]
 };

 return (
 <div className="min-h-screen pt-20">
 <SEO
 title="FAQ — PopCrown Michael Jackson Fan Community | Pricing, Features, Membership"
 description="Frequently asked questions about PopCrown. How to install the free PopCrown app on iPhone &amp; Android, PopCrown Community membership (£1.99/month), what's included, the Legal Vault, and more."
 keywords="PopCrown FAQ, install PopCrown, PopCrown Community, Michael Jackson fan app questions, MJ community help, PopCrown features, Legal Vault"
 canonical="/faq"
 />
 {/* Schema.org JSON-LD for FAQ Page - Rich Snippets */}
 <JsonLd data={faqSchema} />
 <JsonLd data={breadcrumbSchema} />

 {/* Hero Section */}
 <section className="relative py-20 overflow-hidden">
 {/* Background Image - Concert Lights - SEO Optimized */}
 <div 
 className="absolute inset-0 bg-cover bg-center"
 style={{
 backgroundImage: 'url(https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2070)',
 }}
 role="img"
 aria-label="Concert stage lights representing Michael Jackson legendary performances - FAQ and help for MJ fan community members"
 />
 {/* Hidden img for SEO */}
 <img 
 src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=2070"
 alt="Concert stage with dramatic lighting representing Michael Jackson legendary live performances Bad World Tour Dangerous Tour HIStory Tour This Is It - PopCrown FAQ help section for MJ fans King of Pop community questions answered"
 className="sr-only"
 loading="eager"
 />
 <div className="absolute inset-0" style={{
 background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.4) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(225, 6, 0, 0.3) 100%)'
 }} />
 
 <div className="container mx-auto px-4 text-center relative z-10">
 <HelpCircle className="w-16 h-16 text-[#E10600] mx-auto mb-6" />
 <h1 className="text-5xl md:text-6xl font-bold mb-6">
 <span className="text-white">FAQs & </span>
 <span className="text-[#E10600]">Help</span>
 </h1>
 <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
Everything you need to know about PopCrown
 </p>
 <div className="mx-auto h-1 w-32 bg-gradient-to-r from-[#DC143C] via-[#E10600] to-[#DC143C]"></div>
 </div>
 </section>

 {/* Search & Filter Section */}
 <section className="py-8 bg-white sticky top-16 z-40 shadow-md">
 <div className="container mx-auto px-4">
 <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
 {/* Search */}
 <div className="relative w-full md:w-96">
 <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
 <input
 type="text"
 placeholder="Search questions..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
 />
 </div>

 {/* Category Filter */}
 <div className="flex flex-wrap gap-2 justify-center">
 {categories.map(category => (
 <button
 key={category}
 onClick={() => setActiveCategory(category)}
 className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
 activeCategory === category
 ? 'bg-[#DC143C] text-white'
 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
 }`}
 >
 {category}
 </button>
 ))}
 </div>
 </div>
 </div>
 </section>

 {/* FAQ List */}
 <section className="py-16" style={{
 background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 50%, #FFFFFF 100%)'
 }}>
 <div className="container mx-auto px-4 max-w-4xl">
 {filteredFaqs.length === 0 ? (
 <div className="text-center py-12">
 <p className="text-gray-500 text-lg">No questions found matching your search.</p>
 </div>
 ) : (
 <div className="space-y-4">
 {filteredFaqs.map((faq, index) => (
 <div
 key={index}
 className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
 >
 <button
 onClick={() => toggleQuestion(index)}
 className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
 >
 <div className="flex-1 pr-4">
 <span className="inline-block px-2 py-1 bg-[#DC143C]/10 text-[#DC143C] text-xs font-medium rounded mb-2">
 {faq.category}
 </span>
 <h3 className="text-lg font-semibold text-gray-900">
 {faq.question}
 </h3>
 </div>
 {openQuestions[index] ? (
 <ChevronUp className="w-5 h-5 text-[#DC143C] flex-shrink-0" />
 ) : (
 <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
 )}
 </button>
 
 {openQuestions[index] && (
 <div className="px-6 pb-5 border-t border-gray-100">
 <p className="text-gray-600 leading-relaxed pt-4 whitespace-pre-line">
 {faq.answer}
 </p>
 </div>
 )}
 </div>
 ))}
 </div>
 )}

 {/* Results Count */}
 <div className="text-center mt-8 text-gray-500">
Showing {filteredFaqs.length} of {faqs.length} questions
 </div>
 </div>
 </section>

 {/* Contact Section */}
 <section className="py-16 bg-black">
 <div className="container mx-auto px-4 text-center">
 <h2 className="text-3xl font-bold text-white mb-4">
Still need help?
 </h2>
 <p className="text-gray-400 mb-8 max-w-xl mx-auto">
Contact us at support@popcrownsocial.com
 </p>
 <a
 href="https://members.popcrownofficial.com/subscription/join"
 target="_blank"
 rel="noopener noreferrer"
 className="inline-block px-8 py-4 bg-[#E10600] text-black font-bold rounded-full hover:bg-[#FFE44D] transition-colors"
 >
Join PopCrown Community
 </a>
 </div>
 </section>
 </div>
 );
};

export default FAQ;
